import type {
  EmbeddingProviderEntry,
  LlmProviderEntry,
  ProviderTemplate,
  RerankProviderEntry,
} from '../types/api'
import { apiClient } from './client'
import type { LlmConnectionTestResult } from './settings'

// ---------------- Embedding providers ----------------

export async function listEmbeddingProviders(): Promise<EmbeddingProviderEntry[]> {
  const res = await apiClient.get<{ providers: EmbeddingProviderEntry[] }>('/embedding-providers')
  return res.providers
}

export async function createEmbeddingProvider(data: {
  label: string
  provider: string
  apiBaseUrl?: string | null
  apiKey?: string | null
  modelName?: string | null
  promptProfile?: 'nomic' | 'instruct' | null
  extraBody?: Record<string, unknown> | null
  authHeader?: 'authorization_bearer' | 'api_key' | 'none'
  isDefault?: boolean
}): Promise<EmbeddingProviderEntry> {
  return apiClient.post<EmbeddingProviderEntry>('/embedding-providers', data)
}

export async function updateEmbeddingProvider(
  id: number,
  data: {
    label?: string
    apiBaseUrl?: string | null
    apiKey?: string | null
    promptProfile?: 'nomic' | 'instruct' | null
    extraBody?: Record<string, unknown> | null
    authHeader?: 'authorization_bearer' | 'api_key' | 'none'
    isActive?: boolean
    isDefault?: boolean
  }
): Promise<EmbeddingProviderEntry> {
  return apiClient.patch<EmbeddingProviderEntry>(`/embedding-providers/${id}`, data)
}

export async function deleteEmbeddingProvider(id: number): Promise<void> {
  await apiClient.delete(`/embedding-providers/${id}`)
}

export interface ProviderConnectionTestResult {
  ok: true
  dimensions?: number
  latencyMs: number
  message: string
}

export async function listSavedEmbeddingProviderModels(
  id: number,
  data: {
    apiBaseUrl?: string
    apiKey?: string | null
    authHeader?: 'authorization_bearer' | 'api_key' | 'none'
  }
): Promise<string[]> {
  const response = await apiClient.post<{ models: string[] }>(
    `/embedding-providers/${id}/models`,
    data
  )
  return response.models
}

export async function testUnsavedEmbeddingProvider(data: {
  provider: string
  apiBaseUrl?: string | null
  apiKey?: string | null
  modelName?: string | null
  promptProfile?: 'nomic' | 'instruct' | null
  extraBody?: Record<string, unknown> | null
  authHeader?: 'authorization_bearer' | 'api_key'
}): Promise<ProviderConnectionTestResult> {
  return apiClient.post<ProviderConnectionTestResult>('/embedding-providers/test', data)
}

// ---------------- LLM providers ----------------

export async function listLlmProviders(): Promise<LlmProviderEntry[]> {
  const res = await apiClient.get<{ providers: LlmProviderEntry[] }>('/llm-providers')
  return res.providers
}

export async function createLlmProvider(data: {
  label: string
  provider?: 'openai_compatible' | 'openrouter' | null
  apiBaseUrl: string
  apiKey?: string | null
  modelName: string
  extraBody?: Record<string, unknown> | null
  authHeader?: 'authorization_bearer' | 'api_key' | 'none'
  isDefault?: boolean
}): Promise<LlmProviderEntry> {
  return apiClient.post<LlmProviderEntry>('/llm-providers', data)
}

export async function updateLlmProvider(
  id: number,
  data: {
    label?: string
    provider?: 'openai_compatible' | 'openrouter' | null
    apiBaseUrl?: string | null
    apiKey?: string | null
    modelName?: string | null
    extraBody?: Record<string, unknown> | null
    authHeader?: 'authorization_bearer' | 'api_key' | 'none'
    isActive?: boolean
    isDefault?: boolean
  }
): Promise<LlmProviderEntry> {
  return apiClient.patch<LlmProviderEntry>(`/llm-providers/${id}`, data)
}

export async function deleteLlmProvider(id: number): Promise<void> {
  await apiClient.delete(`/llm-providers/${id}`)
}

export async function testSavedLlmProvider(id: number): Promise<LlmConnectionTestResult> {
  return apiClient.post<LlmConnectionTestResult>(`/llm-providers/${id}/test`, {})
}

export async function testUnsavedLlmProvider(data: {
  apiBaseUrl?: string | null
  apiKey?: string | null
  modelName?: string | null
  extraBody?: Record<string, unknown> | null
  authHeader?: 'authorization_bearer' | 'api_key'
}): Promise<LlmConnectionTestResult> {
  return apiClient.post<LlmConnectionTestResult>('/llm-providers/test', data)
}

export async function listSavedLlmProviderModels(
  id: number,
  data: {
    apiBaseUrl?: string
    apiKey?: string | null
    authHeader?: 'authorization_bearer' | 'api_key' | 'none'
  }
): Promise<string[]> {
  const response = await apiClient.post<{ models: string[] }>(`/llm-providers/${id}/models`, data)
  return response.models
}

// ---------------- Rerank providers ----------------

export async function listRerankProviders(): Promise<RerankProviderEntry[]> {
  const res = await apiClient.get<{ providers: RerankProviderEntry[] }>('/rerank-providers')
  return res.providers
}

export async function createRerankProvider(data: {
  label: string
  provider: 'local' | 'openai_compatible' | 'openrouter' | 'deepinfra'
  apiBaseUrl?: string | null
  apiKey?: string | null
  modelName: string
  extraBody?: Record<string, unknown> | null
  authHeader?: 'authorization_bearer' | 'api_key' | 'none'
  isDefault?: boolean
}): Promise<RerankProviderEntry> {
  return apiClient.post<RerankProviderEntry>('/rerank-providers', data)
}

export async function updateRerankProvider(
  id: number,
  data: {
    label?: string
    apiBaseUrl?: string | null
    apiKey?: string | null
    modelName?: string | null
    extraBody?: Record<string, unknown> | null
    authHeader?: 'authorization_bearer' | 'api_key' | 'none'
    isActive?: boolean
    isDefault?: boolean
  }
): Promise<RerankProviderEntry> {
  return apiClient.patch<RerankProviderEntry>(`/rerank-providers/${id}`, data)
}

export async function deleteRerankProvider(id: number): Promise<void> {
  await apiClient.delete(`/rerank-providers/${id}`)
}

export async function testUnsavedRerankProvider(data: {
  provider?: 'local' | 'openai_compatible' | 'openrouter' | 'deepinfra' | null
  apiBaseUrl?: string | null
  apiKey?: string | null
  modelName?: string | null
  extraBody?: Record<string, unknown> | null
  authHeader?: 'authorization_bearer' | 'api_key'
}): Promise<ProviderConnectionTestResult> {
  return apiClient.post<ProviderConnectionTestResult>('/rerank-providers/test', data)
}

export async function listSavedRerankProviderModels(
  id: number,
  data: {
    apiBaseUrl?: string
    apiKey?: string | null
    authHeader?: 'authorization_bearer' | 'api_key' | 'none'
  }
): Promise<string[]> {
  const response = await apiClient.post<{ models: string[] }>(
    `/rerank-providers/${id}/models`,
    data
  )
  return response.models
}

// ---------------- Custom provider templates (superadmin) ----------------

export async function listProviderTemplates(): Promise<ProviderTemplate[]> {
  const res = await apiClient.get<{ templates: ProviderTemplate[] }>('/provider-templates')
  return res.templates
}

export async function createProviderTemplate(data: {
  name: string
  baseUrl: string
  embeddingUrl?: string | null
  rerankUrl?: string | null
  chatUrl?: string | null
  modelsUrl?: string | null
  embeddingModel?: string | null
  chatModel?: string | null
  rerankModel?: string | null
  extraBody?: Record<string, unknown> | null
}): Promise<ProviderTemplate> {
  return apiClient.post<ProviderTemplate>('/provider-templates', data)
}

export async function updateProviderTemplate(
  id: number,
  data: {
    name?: string
    baseUrl?: string | null
    embeddingUrl?: string | null
    rerankUrl?: string | null
    chatUrl?: string | null
    modelsUrl?: string | null
    embeddingModel?: string | null
    chatModel?: string | null
    rerankModel?: string | null
    extraBody?: Record<string, unknown> | null
    isActive?: boolean
  }
): Promise<ProviderTemplate> {
  return apiClient.patch<ProviderTemplate>(`/provider-templates/${id}`, data)
}

export async function deleteProviderTemplate(id: number): Promise<void> {
  await apiClient.delete(`/provider-templates/${id}`)
}

export interface ProviderTemplateProbe {
  ok: boolean
  message?: string
  models?: string[]
  dimensions?: number
  detectedType?: 'openai_compatible' | 'deepinfra' | 'openrouter' | null
}

export interface ProviderTemplateTestResult {
  models: ProviderTemplateProbe
  embedding: ProviderTemplateProbe
  chat: ProviderTemplateProbe
  rerank: ProviderTemplateProbe
}

export async function testProviderTemplate(data: {
  baseUrl: string
  embeddingUrl?: string | null
  rerankUrl?: string | null
  chatUrl?: string | null
  modelsUrl?: string | null
  apiKey?: string | null
  authHeader?: 'authorization_bearer' | 'api_key'
  embeddingModel?: string | null
  chatModel?: string | null
  rerankModel?: string | null
  extraBody?: Record<string, unknown> | null
}): Promise<ProviderTemplateTestResult> {
  return apiClient.post<ProviderTemplateTestResult>('/provider-templates/test', data)
}

export async function listTemplateModels(data: {
  baseUrl?: string | null
  modelsUrl?: string | null
  apiKey?: string | null
  authHeader?: 'authorization_bearer' | 'api_key'
}): Promise<string[]> {
  const response = await apiClient.post<{ models: string[] }>('/provider-templates/models', data)
  return response.models
}

export interface ProviderCatalogLists {
  embedding: string[]
  llm: string[]
  rerank: string[]
}

/**
 * Fetch a DeepInfra-style model catalog (bare array with
 * `model_name`/`reported_type`) and split it per service kind.
 * The Models API URL field doubles as the catalog URL — that is the field
 * the admin pastes `https://api.deepinfra.com/models/list` into.
 */
export async function listTemplateCatalog(catalogUrl: string): Promise<ProviderCatalogLists> {
  return apiClient.post<ProviderCatalogLists>('/provider-templates/catalog', { catalogUrl })
}

// ---------------- Platform settings (superadmin) ----------------

export interface PlatformSettings {
  localEmbeddingEnabled: boolean
  localRerankEnabled: boolean
  defaultEmbeddingProvider: string | null
  defaultEmbeddingApiBaseUrl: string | null
  defaultEmbeddingApiKeySet: boolean
  defaultEmbeddingApiKeyMasked: string | null
  defaultEmbeddingModelName: string | null
  defaultEmbeddingDimensions: number | null
  defaultEmbeddingPromptProfile: string | null
  defaultRerankProvider: string | null
  defaultRerankApiBaseUrl: string | null
  defaultRerankApiKeySet: boolean
  defaultRerankApiKeyMasked: string | null
  defaultRerankModelName: string | null
  updatedAt: string
}

export async function getPlatformSettings(): Promise<PlatformSettings> {
  return apiClient.get<PlatformSettings>('/platform-settings')
}

export async function updatePlatformSettings(data: {
  localEmbeddingEnabled?: boolean
  localRerankEnabled?: boolean
  defaultEmbeddingProvider?: string | null
  defaultEmbeddingApiBaseUrl?: string | null
  defaultEmbeddingApiKey?: string | null
  defaultEmbeddingModelName?: string | null
  defaultEmbeddingDimensions?: number | null
  defaultEmbeddingPromptProfile?: string | null
  defaultRerankProvider?: string | null
  defaultRerankApiBaseUrl?: string | null
  defaultRerankApiKey?: string | null
  defaultRerankModelName?: string | null
}): Promise<PlatformSettings> {
  return apiClient.put<PlatformSettings>('/platform-settings', data)
}
